// =============================
// src/mobile_pages/materials/order/Order_new.jsx
// =============================
import { useEffect, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { useLocation } from "react-router-dom";
import axios from "../../../api/axios";
import OrderSummary from "../../../components/common/materials/OrderSummary";

export default function OrderOfficialForm({
  initial = {},
  projectId,
  docLabel = "발주서",
  readOnly = false,
  onChange,
}) {
  const location = useLocation();
  const psHeader = location.state?.psHeader;
  const psItems = location.state?.psItems;
  const fromDns = location.state?.from;

  // ----------------------------
  // 기본 Header
  // ----------------------------
  const initialHeader = initial.header || initial;

  const [title, setTitle] = useState(
    psHeader?.title
      ? `[발주] ${psHeader.title}`
      : initialHeader.title || `${docLabel}(공식)`
  );
  const [poDate, setPoDate] = useState(
    initialHeader.po_date || new Date().toISOString().slice(0, 10)
  );
  const [supplier, setSupplier] = useState(initialHeader.supplier || "");
  const [reqDate, setReqDate] = useState(initialHeader.request_date || "");
  const [deliveryPlace, setDeliveryPlace] = useState(
    initialHeader.delivery_place || ""
  );
  const [note, setNote] = useState(initialHeader.note || "");
  const project = projectId || initial.project || null;

  // ----------------------------
  // 수급계획(PS) 목록
  // ----------------------------
  const [planList, setPlanList] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await axios.get(`/material/docs/`, {
          params: { project, form_type: "PS" },
        });
        setPlanList(res.data?.results || res.data || []);
      } catch (err) {
        console.error("❌ PS 목록 로드 실패", err);
      }
    }
    load();
  }, [project]);

  // ----------------------------
  // PS 불러오기
  // ----------------------------
  const loadPlanDetail = async (dns) => {
    try {
      const res = await axios.get(`/material/docs/${dns}/`);
      const doc = res.data;

      const header = doc.header || {};
      const items = doc.lines?.map((l) => l.body || l) || [];

      setSelectedPlan(dns);
      setTitle(`[발주] ${header.title || header.note || ""}`);

      setRowsData(
        items.map((r) => ({
          _id: uuid(),
          code: r.code,
          name: r.name,
          spec: r.spec,
          unit: r.unit,
          qty: r.qty || r.plan_qty || r.body?.qty || 0,
          price: 0,
          amount: 0,
          remark: "",
        }))
      );
    } catch (err) {
      console.error("❌ PS 상세 불러오기 실패", err);
    }
  };

  // ----------------------------
  // 되돌리기
  // ----------------------------
  const resetForm = () => {
    setSelectedPlan(null);
    setTitle(`${docLabel}(공식)`);
    setPoDate(new Date().toISOString().slice(0, 10));
    setSupplier("");
    setReqDate("");
    setDeliveryPlace("");
    setNote("");
    setRowsData([makeEmptyRow()]);
  };

  // ----------------------------
  // Row 구조
  // ----------------------------
  const makeEmptyRow = () => ({
    _id: uuid(),
    code: "",
    name: "",
    spec: "",
    unit: "",
    qty: 0,
    price: 0,
    amount: 0,
    remark: "",
  });

  const mappedInitial = psItems
    ? psItems.map((r) => ({
      _id: uuid(),
      code: r.code,
      name: r.name,
      spec: r.spec,
      unit: r.unit,
      qty: r.qty || r.plan_qty || r.body?.qty || 0,
      price: 0,
      amount: 0,
      remark: "",
    }))
    : (initial.items || initial.lines || []).map((r) => ({
      ...r,
      _id: r._id || uuid(),
    }));

  const [rowsData, setRowsData] = useState(
    mappedInitial.length > 0 ? mappedInitial : [makeEmptyRow()]
  );

  const rows = useMemo(
    () =>
      rowsData.map((r) => ({
        ...r,
        amount: +(Number(r.qty || 0) * Number(r.price || 0)).toFixed(2),
      })),
    [rowsData]
  );

  const subtotal = useMemo(
    () => rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows]
  );
  const vat_total = 0;
  const total = subtotal;

  const summaryForm = {
    title,
    po_date: poDate,
    supplier,
    request_date: reqDate,
    delivery_place: deliveryPlace,
    note,
    items: rows.map(({ _id, ...rest }) => rest),
    subtotal,
    vat_total,
    total,
  };

  // ----------------------------
  // 상위 전달
  // ----------------------------
  useEffect(() => {
    if (!onChange) return;
    onChange({
      id: initial.id,
      type: "order",
      project: projectId,
      header: {
        title,
        po_date: poDate,
        supplier,
        request_date: reqDate,
        delivery_place: deliveryPlace,
        note,
        from_ps_dns: fromDns,
      },
      items: rows.map(({ _id, ...rest }) => rest),
      subtotal,
      vat_total,
      total,
      summary_component: <OrderSummary form={summaryForm} />,
      docDescribe: "발주 품목의 수량·단가를 입력해 발주서를 작성하세요.",
    });
  }, [title, poDate, supplier, reqDate, deliveryPlace, note, rows]);

  const addRow = () => setRowsData((prev) => [...prev, makeEmptyRow()]);
  const removeRow = (id) =>
    setRowsData((prev) => {
      const filtered = prev.filter((r) => r._id !== id);
      return filtered.length > 0 ? filtered : [makeEmptyRow()];
    });
  const updateCell = (id, key, value) =>
    setRowsData((prev) =>
      prev.map((r) => (r._id === id ? { ...r, [key]: value } : r))
    );

  // ----------------------------
  // UI (모바일 최적화)
  // ----------------------------
  return (
    <div className="space-y-3 min-w-0 overflow-hidden">
      {/* 🔹 PS 선택 + 되돌리기 */}
      {!readOnly && (
        <div className="flex flex-col gap-2 min-w-0">
          <select
            className="w-full border rounded-lg px-3 py-2 text-sm min-w-0"
            value={selectedPlan || ""}
            onChange={(e) => e.target.value && loadPlanDetail(e.target.value)}
          >
            <option value="" disabled>
              수급 계획 선택
            </option>
            {planList.map((p) => (
              <option key={p.dns} value={p.dns}>
                {p.header?.title || p.dns}
              </option>
            ))}
          </select>

          {selectedPlan && (
            <button className="btn-basic-sm w-full" onClick={resetForm}>
              되돌리기
            </button>
          )}
        </div>
      )}

      {/* 기본 정보 */}
      <div className="space-y-1">
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">제목</label>
          <input
            type="text"
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">발주일</label>
          <input
            type="date"
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={poDate}
            onChange={(e) => setPoDate(e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">
            공급업체
          </label>
          <input
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={supplier}
            onChange={(e) => setSupplier(e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">
            납기 요청일
          </label>
          <input
            type="date"
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={reqDate}
            onChange={(e) => setReqDate(e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">
            납품 장소
          </label>
          <input
            className="w-full border rounded-md px-2 py-1 text-sm"
            value={deliveryPlace}
            onChange={(e) => setDeliveryPlace(e.target.value)}
            disabled={readOnly}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">
            비고 / 메모
          </label>
          <textarea
            className="w-full min-h-[100px] border rounded-md px-2 py-1 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={readOnly}
          />
        </div>
      </div>

      {/* 품목 테이블 */}
      <div className="rounded-lg border border-gray-200 bg-white overflow-hidden min-w-0">
        <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 min-w-0">
          <div className="font-medium text-sm min-w-0 truncate">
            발주 품목
          </div>
          {!readOnly && (
            <button
              className="btn-create-sm flex-shrink-0 whitespace-nowrap"
              onClick={addRow}
            >
              + 행 추가
            </button>
          )}
        </div>
        <div className="space-y-3">
          {rows.map((r) => (
            <div
              key={r._id}
              className="border-2 border-gray-200 rounded-xl p-4 bg-white shadow-sm space-y-3 mb-3"
            >
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">
                  품목 정보
                </span>
                {!readOnly && (
                  <button
                    className="px-3 py-1.5 rounded-md border border-red-300 text-xs font-medium whitespace-nowrap text-red-600 hover:bg-red-50 active:bg-red-100 transition-colors"
                    onClick={() => removeRow(r._id)}
                  >
                    삭제
                  </button>
                )}
              </div>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    코드
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.code}
                    onChange={(e) =>
                      updateCell(r._id, "code", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder="코드 입력"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    품명
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.name}
                    onChange={(e) =>
                      updateCell(r._id, "name", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder="품명 입력"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    규격
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.spec}
                    onChange={(e) =>
                      updateCell(r._id, "spec", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder="규격 입력"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    단위
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.unit}
                    onChange={(e) =>
                      updateCell(r._id, "unit", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder="단위 입력"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    수량
                  </label>
                  <input
                    type="number"
                    className="w-full text-right border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.qty}
                    onChange={(e) => updateCell(r._id, "qty", e.target.value)}
                    disabled={readOnly}
                    placeholder="수량 입력"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    단가
                  </label>
                  <input
                    type="number"
                    className="w-full text-right border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.price}
                    onChange={(e) =>
                      updateCell(r._id, "price", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder="단가 입력"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    금액
                  </label>
                  <div className="text-sm text-right px-3 py-2.5 bg-gray-50 rounded-md border border-gray-200 min-h-[44px] flex items-center justify-end font-medium">
                    {r.amount.toLocaleString()}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    비고
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm min-w-0 min-h-[44px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={r.remark}
                    onChange={(e) =>
                      updateCell(r._id, "remark", e.target.value)
                    }
                    disabled={readOnly}
                    placeholder="비고 입력"
                  />
                </div>
              </div>
            </div>
          ))}
          {rows.length > 0 && (
            <div className="border-t-2 border-gray-300 pt-3 mt-3">
              <div className="space-y-3">
                <div className="text-right font-semibold text-base mb-2 text-gray-700">
                  합계
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-semibold text-gray-700 block">
                    총 금액
                  </label>
                  <div className="text-base text-right px-3 py-2.5 bg-gray-50 rounded-md border border-gray-200 min-h-[44px] flex items-center justify-end font-bold text-gray-900">
                    {total.toLocaleString()} 원
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
